import {
  getOutcomeNoun,
  isRevenueOutcomeDefinition,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import type {
  PlaybookCriterionNode,
  PlaybookId,
  PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import type {
  PlaybookNodeSampleToolResultAggregates,
  PlaybookNodeSampleToolResultSideAggregates,
} from "@repo/app-types/entities/playbookNodeSampleToolResult";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useState } from "react";
import { useLazyFetchSampleToolResultGradingsQuery } from "../../api/entities/playbook.js";
import type { SampleToolResultCriterionEntry } from "../../hooks/useSampleToolResults.js";
import { CallVolumeByCriteriaScoreComparisonChart } from "./callVolumeByCriteriaScoreComparisonChart.js";
import { CriteriaHealthCheckCard } from "./criteriaHealthCheckCard.js";
import {
  GradedCallsSlideover,
  type GradedCallsTab,
} from "./gradedCallsSlideover.js";
import { OutcomePotentialComparisonCard } from "./outcomePotentialComparisonCard.js";

interface SuggestionResultsProps {
  /**
   * Undefined when no saved sample-tool result exists for the criterion
   * yet — the pane then renders the empty placeholder so the column stays
   * present in the editor layout.
   */
  aggregates: PlaybookNodeSampleToolResultAggregates | undefined;
  outcomeDefinition: OutcomeDefinition | undefined;
  /** The full sample tool result entry, used to fetch grading details for the slideover. */
  suggestion?: SampleToolResultCriterionEntry;
  playbookId?: PlaybookId | null;
  playbookNodeId: PlaybookNodeId;
  gradingValueType: PlaybookCriterionNode["grading_value_type"];
}

export const SuggestionResults: FC<SuggestionResultsProps> = ({
  aggregates,
  outcomeDefinition,
  suggestion,
  playbookId,
  playbookNodeId,
  gradingValueType,
}) => {
  const [isSlideoverOpen, setIsSlideoverOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<GradedCallsTab>("sample");
  const [fetchGradings, { data: gradingsData, isFetching, isError }] =
    useLazyFetchSampleToolResultGradingsQuery();

  const openGradedCalls = (tab: GradedCallsTab) => {
    if (playbookId && suggestion) {
      void fetchGradings({
        playbookId,
        sampleToolResultId: suggestion.id,
      });
    }
    setSelectedTab(tab);
    setIsSlideoverOpen(true);
  };

  // Empty placeholder when we don't have a saved sample-tool result yet,
  // or when the playbook lacks an outcome definition (without it the card
  // and chart can't be labeled). For `new_criterion` proposals
  // `aggregates.baseline` is null — the components below degrade to a
  // single-side (tested-only) layout.
  if (!aggregates || !outcomeDefinition) {
    return (
      <div className="flex min-h-[160px] flex-1 items-center justify-center text-center">
        <p className="max-w-[220px] text-xs text-muted-foreground">
          The test results of your criteria will appear here.
        </p>
      </div>
    );
  }

  const isRevenue = isRevenueOutcomeDefinition(outcomeDefinition);
  const outcomeNoun = getOutcomeNoun(outcomeDefinition);
  const baseline = aggregates.baseline;

  const hasPlaybookContext =
    suggestion !== undefined && playbookId !== undefined && playbookId !== null;

  // Eager signal: server keeps `matches_team_feedback.status === "not_applicable"`
  // in lock-step with `breakdown` { correct: 0, close: 0, incorrect: 0 }, so any
  // other status implies at least one classifiable feedback re-grade exists.
  const hasFeedbackGrades =
    isNotNullNotUndefined(suggestion?.health_check) &&
    suggestion.health_check.matches_team_feedback.status !== "not_applicable";

  const hasSampleGrades = aggregates.sample_size > 0;

  const showGradedCallsButton =
    hasPlaybookContext && (hasSampleGrades || hasFeedbackGrades);

  const onViewFeedback =
    hasPlaybookContext && hasFeedbackGrades
      ? () => openGradedCalls("feedback")
      : undefined;

  // `tested.auc` is null when every non-N/A outcome in the sampled window
  // is the same color — AUC and conversion-rate signal aren't computable
  // in that case, so hide the card. The distribution chart and graded-calls
  // list don't depend on AUC and stay visible.
  const testedAuc = aggregates.tested.auc;
  const showOutcomePotentialCard = testedAuc !== null;

  return (
    <>
      <div className="space-y-4">
        {suggestion?.health_check ? (
          <CriteriaHealthCheckCard
            healthCheck={suggestion.health_check}
            onViewFeedback={onViewFeedback}
          />
        ) : null}

        {showOutcomePotentialCard ? (
          <OutcomePotentialComparisonCard
            baselineAuc={baseline?.auc ?? undefined}
            testedAuc={testedAuc}
            baselineNetConversions={
              baseline ? pickNetForCopy(baseline, isRevenue) : undefined
            }
            testedNetConversions={pickNetForCopy(aggregates.tested, isRevenue)}
            averageRevenuePerOpp={aggregates.average_revenue_per_opp}
            outcomeNoun={outcomeNoun}
            outcomePluralNoun={outcomeDefinition.plural_noun}
            isRevenueOutcome={isRevenue}
          />
        ) : null}

        <div className="rounded-md bg-white px-4 pt-3 pb-4 shadow-sm ring-1 ring-black/5">
          <CallVolumeByCriteriaScoreComparisonChart
            baseline={baseline}
            tested={aggregates.tested}
            gradingValueType={aggregates.grading_value_type}
          />
        </div>

        {showGradedCallsButton ? (
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.MEDIUM}
            onClick={() => openGradedCalls("sample")}
            className="w-full justify-center"
          >
            See graded calls
          </Button>
        ) : null}
      </div>

      <GradedCallsSlideover
        isOpen={isSlideoverOpen}
        onClose={() => setIsSlideoverOpen(false)}
        selectedTab={selectedTab}
        onSelectTab={setSelectedTab}
        gradingValueType={gradingValueType}
        gradings={
          gradingsData?.node_type === "criterion" ? gradingsData : undefined
        }
        isLoading={isFetching}
        isError={isError}
      />
    </>
  );
};

/**
 * `getOutcomeImpactCopy` interprets its first argument as `net_revenue per 1k`
 * for revenue outcomes and `net_conversions per 1k` otherwise. Mirror that
 * here so the comparison card receives the right value per outcome type.
 */
function pickNetForCopy(
  side: PlaybookNodeSampleToolResultSideAggregates,
  isRevenue: boolean,
): number | null {
  if (isRevenue) {
    return side.net_revenue ?? null;
  }
  return side.net_conversions;
}
